import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  Skeleton,
  Snackbar,
  Alert,
  Stack,
  Typography,
} from "@mui/material";
import { ThemeProvider, keyframes, useTheme } from "@mui/material/styles";
import TrustNavBar from "./components/TrustNavBar";
import TrustFooter from "./components/TrustFooter";
import SearchPalette from "./components/SearchPalette";
import PublicHero from "./components/PublicHero";
import FrameworkStrip from "./components/FrameworkStrip";
import ControlsSection, { domainElementId } from "./components/ControlsSection";
import ResourcesSection from "./components/ResourcesSection";
import ResourcesSummary from "./components/ResourcesSummary";
import {
  ControlsPreview,
  SubprocessorsPreview,
} from "./components/OverviewDigest";
import { accessOf, isEmailed, isListed, isUnlocked } from "./data/resources";
import { publishedUpdates } from "./data/updates";
import SubprocessorsSection from "./components/SubprocessorsSection";
import FaqSection from "./components/FaqSection";
import UpdatesSection from "./components/UpdatesSection";
import RequestAccessDialog from "./components/RequestAccessDialog";
import EmailDocumentDialog from "./components/EmailDocumentDialog";
import DocumentViewer from "./components/DocumentViewer";
import { useTrustCenter } from "./hooks/useTrustCenter";
import {
  clearVisitorSession,
  readVisitorSession,
  writeVisitorSession,
} from "./data/visitorSession";
import {
  fetchPublicDownload,
  redeemTrustCenterAccess,
  signOutVisitor,
  startDownload,
} from "../../apis/trust-center-apis/trustCenterApis";
import { styles } from "./styles/publicStyles";
import { createPublicTheme, usePublicFont } from "./styles/publicTheme";
import { isCustomDomainHost } from "./data/publishing";
import { brandOf, fontStack } from "./data/brand";
import useBrandFont from "./hooks/useBrandFont";
import { companyLogoUrl } from "../../apis/company-logo-apis/companyLogoApis";

// Public page. Rendered outside Auth0 (see src/index.js), so it must not use
// Apollo or anything that needs a logged-in user. The admin preview passes
// its own data in as `preview` (see TrustCenterPreview).
// Which Trust Center this is. On Regodit's hosts: /trust/<company-domain>.
// On a customer's own domain (trust.acme.com) the whole host is theirs: the
// page is at "/" and the API finds it by host (see siteParams in the API).
const onCustomDomain = isCustomDomainHost();
const getDomainFromPath = () =>
  onCustomDomain
    ? window.location.hostname.toLowerCase()
    : decodeURIComponent(window.location.pathname.split("/")[2] || "");
// Where the page's own paths start: "" on a custom domain.
const basePath = (domain) => (onCustomDomain ? "" : `/trust/${domain}`);

// Tabs like Vanta's. Each has its own URL: /trust/<company-domain>/<tab>
// (or /<tab> on a custom domain).
export const TRUST_TABS = [
  { id: "overview", label: "Overview" },
  { id: "resources", label: "Resources" },
  { id: "controls", label: "Controls" },
  { id: "subprocessors", label: "Subprocessors" },
  { id: "faq", label: "FAQ" },
  { id: "updates", label: "Updates" },
];

// Tabs with something in them (Overview always): a tab the company has
// nothing in (no updates posted, no FAQ, ...) is not shown at all.
const tabHasContent = (id, data, linkedResourceId) => {
  if (!data) return id === "overview";
  switch (id) {
    case "resources":
      return (data.resources || []).some(
        (r) => isListed(r) || r.id === linkedResourceId
      );
    case "controls":
      return (data.controls || []).length > 0;
    case "subprocessors":
      return (data.subprocessors || []).length > 0;
    case "faq":
      return (data.faqs || []).length > 0;
    case "updates":
      return publishedUpdates(data.updates).length > 0;
    default:
      return true;
  }
};

// Under each tab's title (every tab but Overview, which has the banner).
const TAB_INTROS = {
  resources: (name) =>
    `Security documents, reports and policies from ${name}. Some open after ${name} approves a request.`,
  controls: (name) =>
    `The safeguards ${name} has in place, checked against its live systems.`,
  subprocessors: (name) =>
    `Companies that process customer data for ${name}, what they do, and where the data is stored.`,
  faq: (name) =>
    `Answers from ${name} to common security and privacy questions.`,
  updates: (name) => `Security and compliance news from ${name}.`,
};

// Title and one line about the tab, then a rule (like Delve's inner pages).
const TabHeading = ({ tab, company }) => {
  const theme = useTheme();
  const label = TRUST_TABS.find((t) => t.id === tab)?.label;
  const intro = TAB_INTROS[tab]?.(company?.name || "This company");
  return (
    <Box
      sx={{
        pb: { xs: 2.5, md: 3 },
        mb: { xs: 2.5, md: 3 },
        borderBottom: `1px solid ${theme.trust.line}`,
      }}
    >
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: 26, md: 32 },
          fontWeight: 600,
          letterSpacing: "-0.025em",
          lineHeight: 1.15,
          color: theme.trust.ink,
        }}
      >
        {tab === "faq" ? "Frequently asked questions" : label}
      </Typography>
      {intro && (
        <Typography
          sx={{
            mt: 1,
            fontSize: 15,
            lineHeight: 1.6,
            color: theme.trust.inkSoft,
            maxWidth: 720,
          }}
        >
          {intro}
        </Typography>
      )}
    </Box>
  );
};

const readTab = () => {
  const linked = new URLSearchParams(window.location.search).has("resource");
  const tab =
    window.location.pathname.split("/")[onCustomDomain ? 1 : 3] ||
    (linked ? "resources" : "overview");
  return TRUST_TABS.some((t) => t.id === tab) ? tab : "overview";
};

// ?resource=<id> from a "Copy link" URL.
const readLinkedResource = () => {
  const id = Number(
    new URLSearchParams(window.location.search).get("resource")
  );
  return Number.isInteger(id) && id > 0 ? id : null;
};

const NotAvailable = () => {
  const theme = useTheme();
  return (
    <Card sx={{ ...styles.mainCard, p: 4, textAlign: "center" }}>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>
        This Trust Center is not available
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: theme.customColors.text.secondary }}
      >
        The company has not published it yet. Check the link, or ask your
        contact at the company.
      </Typography>
    </Card>
  );
};

// `label`: what is loading, said to screen readers and shown above.
const PageSkeleton = ({ label }) => (
  <Stack spacing={3} role="status" aria-label={label || "Loading"}>
    {label && (
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    )}
    <Skeleton variant="text" width="40%" height={48} />
    <Skeleton variant="text" width="80%" />
    <Skeleton variant="rounded" height={180} />
    <Skeleton variant="rounded" height={320} />
  </Stack>
);

// `onPreviewRefresh` reloads the admin preview's data and `onPreviewDownload`
// fetches files through the signed-in API (TrustCenterPreview), so an
// unpublished page can be checked too.
// Tab content eases in when the tab changes (not under reduced motion).
const tabIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const TrustCenterPageContent = ({
  preview,
  onPreviewRefresh,
  onPreviewDownload,
}) => {
  const theme = useTheme();
  const domain = getDomainFromPath();
  const isPreview = preview !== undefined;
  const [linkedResourceId, setLinkedResourceId] = useState(readLinkedResource);
  const [visitorSession, setVisitorSession] = useState(() =>
    isPreview ? null : readVisitorSession(domain)
  );
  const visitorToken = visitorSession?.token || null;
  const { data, loading, error, refresh } = useTrustCenter(domain, {
    resourceId: linkedResourceId,
    preview,
    token: visitorToken,
  });
  const [requestOpen, setRequestOpen] = useState(false);
  // A requestable resource clicked before access is granted.
  const [pendingResource, setPendingResource] = useState(null);
  const [toast, setToast] = useState("");
  const [toastSeverity, setToastSeverity] = useState("info");
  const notify = (message, severity = "info") => {
    setToastSeverity(severity);
    setToast(message);
  };
  const [tab, setTab] = useState(readTab);
  const [searchOpen, setSearchOpen] = useState(false);
  // The update or subprocessor picked in the search ({ tab, id, at }): its
  // tab shows it even if it is on a later page.
  const [searchFocus, setSearchFocus] = useState(null);
  const focusOn = (focusTab) =>
    searchFocus?.tab === focusTab ? searchFocus : null;
  // The page scrolls inside its own box. The nav bar floats at its top and
  // gains a shadow once the page has scrolled.
  const pageRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const onScroll = () => setScrolled(page.scrollTop > 8);
    page.addEventListener("scroll", onScroll, { passive: true });
    return () => page.removeEventListener("scroll", onScroll);
  }, []);
  // ⌘K / Ctrl+K opens search from anywhere on the page.
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // A new tab starts at the top of the page. Smooth unless the visitor asks
  // for less motion.
  const scrollToTop = () => {
    const page = pageRef.current;
    if (!page || !page.scrollTop || typeof page.scrollTo !== "function") return;
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    page.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };
  const pageUrl = `${window.location.origin}${basePath(domain)}`;

  // Signed in after a grant link or a request. The page itself never asks
  // for a code or an NDA.
  // Only tabs with content; a link to a hidden one shows the Overview.
  const shownTabs = TRUST_TABS.filter((t) =>
    tabHasContent(t.id, data, linkedResourceId)
  );
  const activeTab = shownTabs.some((t) => t.id === tab) ? tab : "overview";
  const signedIn = !!data?.visitor?.email && !!visitorToken;
  // Requestable files open after an admin approved a request for that file
  // (approval is per document). The grant link's session carries the
  // approved ids; a later page payload must not put the lock back on them.
  const access = accessOf(data?.visitor, visitorSession);
  const showContent = !!data || isPreview;

  // Personal access link from a grant email:
  // /trust/<slug>/resources?resource=<id>&access=<token>
  // The resource query stays, so Resources opens on that file.
  useEffect(() => {
    if (isPreview || !domain) return;
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get("access");
    if (!accessToken) return;
    params.delete("access");
    const next = `${window.location.pathname}${
      params.toString() ? `?${params}` : ""
    }${window.location.hash}`;
    window.history.replaceState(null, "", next);

    let cancelled = false;
    (async () => {
      try {
        const session = await redeemTrustCenterAccess({
          slug: domain,
          accessToken,
        });
        if (cancelled) return;
        // Redeem only succeeds for an approved grant, so this file opens
        // with View and Download instead of Request access.
        const granted = { ...session, privateAccess: true };
        writeVisitorSession(domain, granted);
        setVisitorSession(granted);
        notify(`Signed in as ${session.email}.`, "success");
        // useTrustCenter refetches when visitorToken updates.
      } catch (err) {
        if (!cancelled)
          notify(err.message || "That access link is not valid.", "error");
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only run once when the page loads with ?access=
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, isPreview]);

  // Back / Forward switch tabs too.
  useEffect(() => {
    const onPop = () => setTab(readTab());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Controls follow the frameworks shown on the page, and their status is
  // live. So each time a visitor opens the Controls tab (not on the first
  // load, which is fresh) the page data is fetched again in the background:
  // a buyer who opened the page earlier gets the current controls, e.g.
  // without a framework the admin has since hidden. The list on screen stays
  // until the new one arrives; a failed fetch keeps it.
  const openedTab = useRef(tab);
  useEffect(() => {
    if (tab !== "controls" || openedTab.current === "controls") {
      openedTab.current = tab;
      return;
    }
    openedTab.current = tab;
    const reload = isPreview ? onPreviewRefresh : refresh;
    if (reload) Promise.resolve(reload()).catch(() => {});
    // Only when the tab changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // A framework's report (Overview, search): open Resources on that file.
  const goToResource = (resourceId) => {
    const params = new URLSearchParams();
    if (isPreview) params.set("preview", "1");
    params.set("resource", String(resourceId));
    window.history.pushState(
      null,
      "",
      `${basePath(domain)}/resources?${params}`
    );
    setTab("resources");
    setLinkedResourceId(Number(resourceId));
  };

  const goToTab = (next, categoryId, { scroll = true } = {}) => {
    const query = isPreview ? "?preview=1" : "";
    const path = next === "overview" ? "" : `/${next}`;
    window.history.pushState(
      null,
      "",
      `${basePath(domain)}${path || (onCustomDomain ? "/" : "")}${query}`
    );
    setTab(next);
    if (!categoryId && scroll) setTimeout(scrollToTop);
    if (categoryId) {
      setTimeout(() =>
        document
          .getElementById(`category-${categoryId}`)
          ?.scrollIntoView({ block: "start" })
      );
    }
  };

  // `tokenOverride` is used right after sign-in, before React re-renders with
  // the new session in state.
  const download = async (
    resourceId,
    resourceIds = null,
    tokenOverride = undefined
  ) => {
    if (!resourceId) notify("Preparing your download…");
    try {
      const link = isPreview
        ? await onPreviewDownload({ resourceId, resourceIds })
        : await fetchPublicDownload({
            slug: domain,
            resourceId,
            resourceIds,
            token: tokenOverride !== undefined ? tokenOverride : visitorToken,
          });
      startDownload(link.url);
      if (!resourceId)
        notify(
          `Downloading ${link.count} ${link.count === 1 ? "file" : "files"}.`,
          "success"
        );
      return true;
    } catch (err) {
      notify(err.message || "Could not download. Try again.", "error");
      return false;
    }
  };

  const openResource = (resource, tokenOverride = undefined) => {
    if (resource.type === "link") {
      window.open(resource.url, "_blank", "noopener");
      return;
    }
    download(resource.id, null, tokenOverride);
  };

  const handleBulkDownload = (resourceIds) => download(null, resourceIds);

  // View: the file in a viewer on this page. { resource, link } while open;
  // link is null until the API answers.
  const [viewing, setViewing] = useState(null);
  const viewResource = async (resource) => {
    setViewing({ resource, link: null });
    try {
      const link = isPreview
        ? await onPreviewDownload({ resourceId: resource.id, view: true })
        : await fetchPublicDownload({
            slug: domain,
            resourceId: resource.id,
            view: true,
            token: visitorToken,
          });
      setViewing((current) =>
        current?.resource.id === resource.id ? { resource, link } : current
      );
    } catch (err) {
      setViewing(null);
      notify(err.message || "Could not open the file. Try again.", "error");
    }
  };

  // An Email only document being sent (its dialog is open).
  const [emailing, setEmailing] = useState(null);

  const handleOpen = (resource) => {
    if (isEmailed(resource)) {
      setEmailing(resource);
      return;
    }
    if (isUnlocked(resource, access)) {
      openResource(resource);
      return;
    }
    // Ask the company. If they accept, they email the document.
    setPendingResource(resource);
    setRequestOpen(true);
  };

  // Locked documents a visitor can ask for in one request (the nav bar's
  // Request access, or a locked file, where it starts ticked).
  const lockedDocuments = (data?.resources || [])
    .filter((r) => isListed(r) && !isUnlocked(r, access))
    .map((r) => ({
      id: r.id,
      name: r.name,
      category: (data.resourceCategories || []).find(
        (c) => c.id === r.categoryId
      )?.name,
    }));
  const openGeneralRequest = () => {
    setPendingResource(null);
    setRequestOpen(true);
  };

  // A search result: documents open (or ask for access), the rest go to
  // their tab; a control scrolls to its area, an update opens that post.
  const handleSearchSelect = (action) => {
    if (action.type === "resource") handleOpen(action.resource);
    else if (action.type === "report") goToResource(action.resourceId);
    else if (action.type === "tab") goToTab(action.tab);
    else if (action.type === "update" || action.type === "subprocessor") {
      const focusTab = action.type === "update" ? "updates" : "subprocessors";
      // The row scrolls itself into view, so the tab does not scroll to top.
      goToTab(focusTab, null, { scroll: false });
      setSearchFocus({ tab: focusTab, id: action.id, at: Date.now() });
    } else if (action.type === "control") {
      goToTab("controls");
      setTimeout(
        () =>
          document
            .getElementById(domainElementId(action.control.domain))
            ?.scrollIntoView?.({ block: "start" }),
        300
      );
    }
  };

  const handleSignOut = async () => {
    try {
      if (visitorToken)
        await signOutVisitor({ slug: domain, token: visitorToken });
    } catch {
      // Clear local session even if the API call fails.
    }
    clearVisitorSession(domain);
    setVisitorSession(null);
  };

  // The company's branding (as published) for the whole page once the page
  // data is in: colours, font (Google Fonts, or the company's own files)
  // and favicon.
  // Regodit's defaults until then.
  const brand = brandOf(data?.company);
  const fontFamily = fontStack(brand.font);
  useBrandFont(brand.font);
  const brandTheme = useMemo(
    () => (outer) =>
      createPublicTheme(outer, {
        accent: brand.accent,
        secondary: brand.secondary,
        fontFamily,
      }),
    [brand.accent, brand.secondary, fontFamily]
  );
  // The favicon in the browser tab (the preview opens in its own tab too).
  // Browsers do not reliably pick up a changed href, so the app's icons are
  // swapped out for a new link and put back on leaving.
  const faviconUrl = companyLogoUrl(brand.faviconPath);
  useEffect(() => {
    if (!faviconUrl) return undefined;
    const previous = [...document.querySelectorAll("link[rel~='icon']")];
    previous.forEach((link) => link.remove());
    const icon = document.createElement("link");
    icon.rel = "icon";
    icon.href = faviconUrl;
    document.head.appendChild(icon);
    return () => {
      icon.remove();
      previous.forEach((link) => document.head.appendChild(link));
    };
  }, [faviconUrl]);

  return (
    <ThemeProvider theme={brandTheme}>
      <Box ref={pageRef} sx={{ ...styles.page, fontFamily }}>
        {data && showContent && !loading && !error && (
          <TrustNavBar
            company={data.company}
            tabs={shownTabs}
            tab={activeTab}
            onTab={goToTab}
            onSearch={() => setSearchOpen(true)}
            onRequest={lockedDocuments.length > 0 ? openGeneralRequest : null}
            verifiedEmail={signedIn ? data.visitor?.email : null}
            onSignOut={signedIn ? handleSignOut : null}
            scrolled={scrolled}
          />
        )}

        {loading || error || !data || !showContent ? (
          <Stack spacing={2.5} sx={styles.pageInner}>
            {loading ? (
              <PageSkeleton
                label={isPreview ? "Loading your preview…" : null}
              />
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                Could not load this page. Refresh to try again.
              </Alert>
            ) : !data ? (
              <NotAvailable />
            ) : null}
          </Stack>
        ) : (
          <>
            {isPreview &&
              (!data.isPublished || data.draft?.hasUnpublishedChanges) && (
                <Box sx={{ ...styles.pageInner, pb: 0 }}>
                  {!data.isPublished ? (
                    <Alert severity="info" sx={{ borderRadius: "12px" }}>
                      Preview. This page is not published yet, so buyers cannot
                      see it.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                      Preview of your changes. Buyers still see the last
                      published version until you click Publish changes.
                    </Alert>
                  )}
                </Box>
              )}
            {activeTab === "overview" && (
              <PublicHero data={data} onOpenTab={goToTab} />
            )}

            <Box
              key={activeTab}
              sx={{
                ...styles.pageInner,
                pt: { xs: 2.5, md: activeTab === "overview" ? 3 : 4 },
                // Room above the footer (which has no margin of its own).
                pb: { xs: 5, md: 7 },
                animation: `${tabIn} 260ms ${theme.trust.ease}`,
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }}
            >
              {activeTab !== "overview" && (
                <TabHeading tab={activeTab} company={data.company} />
              )}
              {activeTab === "overview" && (
                // Frameworks first (what buyers ask for), then documents and
                // vendors side by side, then the controls.
                <Stack spacing={2.5}>
                  <FrameworkStrip
                    frameworks={data.frameworks}
                    resources={data.resources}
                    onOpenReport={goToResource}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2.5,
                      alignItems: "start",
                      // minmax(0, 1fr): long text must not widen a column.
                      // Resources take the full width when there are no
                      // subprocessors to show beside them.
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        md:
                          (data.subprocessors || []).length > 0
                            ? "minmax(0, 3fr) minmax(0, 2fr)"
                            : "minmax(0, 1fr)",
                      },
                    }}
                  >
                    <ResourcesSummary
                      categories={data.resourceCategories}
                      resources={data.resources}
                      access={access}
                      onOpen={handleOpen}
                      onViewAll={(categoryId) =>
                        goToTab("resources", categoryId)
                      }
                    />
                    <SubprocessorsPreview
                      subprocessors={data.subprocessors}
                      onViewAll={() => goToTab("subprocessors")}
                    />
                  </Box>
                  <ControlsPreview
                    controls={data.controls}
                    onViewAll={() => goToTab("controls")}
                  />
                </Stack>
              )}
              {activeTab === "resources" && (
                <ResourcesSection
                  categories={data.resourceCategories}
                  resources={data.resources}
                  access={access}
                  pageUrl={pageUrl}
                  linkedResourceId={linkedResourceId}
                  onOpen={handleOpen}
                  onBulkDownload={handleBulkDownload}
                  onView={viewResource}
                  onCopied={() => notify("Link copied.")}
                  company={data.company}
                />
              )}
              {activeTab === "controls" && (
                <ControlsSection controls={data.controls} />
              )}
              {activeTab === "subprocessors" && (
                // Shows its own empty state when none are listed.
                <SubprocessorsSection
                  subprocessors={data.subprocessors || []}
                  company={data.company}
                  focus={focusOn("subprocessors")}
                />
              )}
              {activeTab === "faq" && (
                <FaqSection faqs={data.faqs} company={data.company} />
              )}
              {activeTab === "updates" && (
                <UpdatesSection
                  updates={data.updates}
                  company={data.company}
                  focus={focusOn("updates")}
                />
              )}
            </Box>
            <TrustFooter company={data.company} />
          </>
        )}

        {data && (
          <RequestAccessDialog
            open={requestOpen}
            onClose={() => {
              setRequestOpen(false);
              setPendingResource(null);
            }}
            slug={domain}
            companyName={data.company?.name}
            resourceId={pendingResource?.id}
            resourceName={pendingResource?.name}
            documents={lockedDocuments}
            onSubmitted={(result) =>
              notify(result?.message || "Request sent.", "success")
            }
          />
        )}

        {data && (
          <EmailDocumentDialog
            resource={emailing}
            slug={domain}
            companyName={data.company?.name}
            preview={isPreview}
            onClose={() => setEmailing(null)}
            onSent={(message) => notify(message, "success")}
          />
        )}

        {data && (
          <SearchPalette
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            data={data}
            access={access}
            tabs={shownTabs}
            onSelect={handleSearchSelect}
          />
        )}

        <DocumentViewer
          resource={viewing?.resource}
          link={viewing?.link}
          onDownload={(resource) => download(resource.id)}
          onClose={() => setViewing(null)}
        />

        <Snackbar
          open={!!toast}
          autoHideDuration={3000}
          // A click elsewhere (e.g. the next Download) must not wipe the new message.
          onClose={(event, reason) => reason !== "clickaway" && setToast("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={toastSeverity} onClose={() => setToast("")}>
            {toast}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

// The public page has its own look (styles/publicTheme.js); the admin keeps
// the app theme. The accent is the one brand colour (admin-customisable
// later).
const TrustCenterPage = (props) => {
  usePublicFont();
  return (
    <ThemeProvider theme={(outer) => createPublicTheme(outer)}>
      <TrustCenterPageContent {...props} />
    </ThemeProvider>
  );
};

export default TrustCenterPage;
